import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-sql';

import { Button as AppButton } from 'components/button';
import { PageSection } from 'components/section';
import { useElastic } from 'features/my-searches/hooks';
import { useSearchPageContext } from 'features/search-page/SearchPageContext';
import { filterFormat } from 'features/search-page/utils';
import { highlight, languages } from 'prismjs';
import React from 'react';
import { FaCaretSquareDown, FaCheckSquare, FaExclamationCircle, FaPlay } from 'react-icons/fa';
import { FaSave } from 'react-icons/fa';
import {
  FaBookmark,
  FaCheck,
  FaCircleCheck,
  FaCircleXmark,
  FaDownLeftAndUpRightToCenter,
  FaGears,
  FaMagnifyingGlass,
  FaSquarePlus,
  FaToggleOff,
  FaToggleOn,
  FaUpRightAndDownLeftFromCenter,
  FaWandMagicSparkles,
} from 'react-icons/fa6';
import { IoIosCog, IoMdRefresh } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import Editor from 'react-simple-code-editor';
import { toast } from 'react-toastify';
import { useApp, useContent, useFilters } from 'store/hooks';
import { useProfileStore } from 'store/slices';
import {
  Button,
  Claim,
  Col,
  FilterAIPrompt,
  getCookie,
  IFilterAIGenerateResultModel,
  IFilterModel,
  IFilterSettingsModel,
  RadioGroup,
  Row,
  setCookie,
  Show,
  Text,
  ToggleButton,
  ToggleGroup,
  useFilterAIGenerationGuard,
} from 'tno-core';

import {
  AccordionPanel,
  AdvancedSearchSections,
  ElasticQueryHelp,
  ExpandableRow,
  InfoAdvancedQueryOperators,
  InfoDefaultOperator,
  MoreOptions,
  SearchInGroup,
  ToggleFilterStyleInfo,
} from './components';
import {
  defaultAdvancedSearch,
  defaultFilter,
  displayFiltersAsDropdownCookieKey,
} from './constants';
import { AdvancedQueryOperatorOptions, SearchOperatorOptions } from './constants';
import { ManualSearchForm } from './ManualSearchForm';
import * as styled from './styled';

export interface IAdvancedSearchProps {
  /** Event fires when search button is clicked. */
  onSearch?: (filter: IFilterSettingsModel) => void;

  /** Function to update the search terms state */
  setSearchFilter: React.Dispatch<React.SetStateAction<IFilterSettingsModel | null>>;

  validateQuery?: (filter: IFilterSettingsModel) => void;
  queryValidateResult: string[];
}

/***
 * AdvancedSearch is a component that displays the advanced search form in the sidebar.
 * @param expanded - determines whether the advanced search form is expanded or not
 * @param setExpanded - function that controls the expanded state of the advanced search form
 */
export const AdvancedSearch: React.FC<IAdvancedSearchProps> = ({
  onSearch,
  setSearchFilter,
  validateQuery,
  queryValidateResult,
}) => {
  const navigate = useNavigate();
  const [{ myFilters }, { addFilter, updateFilter, generateFilter, chatFilter }] = useFilters();
  const [
    {
      search: { filter: search },
    },
    { storeSearchFilter, findContentWithElasticsearch },
  ] = useContent();

  const genQuery = useElastic();
  const { expanded, setExpanded } = useSearchPageContext();
  const [{ userInfo }] = useApp();
  const [{ filter: activeFilter }, { storeFilter }] = useProfileStore();
  const [originalFilterSettings, setOriginalFilterSettings] =
    React.useState<IFilterSettingsModel>();

  const [searchName, setSearchName] = React.useState<string>(activeFilter?.name ?? '');
  // The prompt that generates the search, saved with it.
  const [prompt, setPrompt] = React.useState<string>(activeFilter?.prompt ?? '');
  // The accordion at the top: search terms (open by default) or the AI prompt.
  const [openPanel, setOpenPanel] = React.useState<'search' | 'ai' | undefined>('search');

  const [displayFiltersAsDropdown, setDisplayFiltersAsDropdown] = React.useState<boolean>(() => {
    const cookieVal = getCookie(displayFiltersAsDropdownCookieKey);
    return !!cookieVal;
  });
  const handleChangeDisplayFiltersAsDropdown = React.useCallback(
    async (displayFiltersAsDropdown: boolean) => {
      setDisplayFiltersAsDropdown(displayFiltersAsDropdown);
      if (displayFiltersAsDropdown) {
        setCookie(displayFiltersAsDropdownCookieKey, true);
      } else {
        setCookie(displayFiltersAsDropdownCookieKey, false, {
          expires: 'Thu, 01 Jan 1970 00:00:00 UTC',
        });
      }
    },
    [setDisplayFiltersAsDropdown],
  );

  const isAdmin = userInfo?.roles.includes(Claim.administrator);
  const [initialState, setInitialState] = React.useState<IFilterSettingsModel | null>(search);
  const [isFirstLoad, setIsFirstLoad] = React.useState<boolean>(true);
  const [searchTerms, setSearchTerms] = React.useState<string>(search?.search ?? '');

  const isDiff = React.useMemo(
    () =>
      prompt !== (activeFilter?.prompt ?? '') ||
      initialState?.search !== searchTerms ||
      JSON.stringify(initialState) !== JSON.stringify(search),
    [initialState, search, searchTerms, prompt, activeFilter?.prompt],
  );

  const checkDisplayOptions = React.useCallback((): boolean => {
    return (
      !search.searchUnpublished ||
      search.boldKeywords ||
      (search.actions?.length ?? 0) > 0 ||
      (search.mediaTypeIds?.length ?? 0) > 0 ||
      search.size !== 500
    );
  }, [search]);

  const handleSearch = async (updatedFilterSettings: IFilterSettingsModel) => {
    onSearch?.(updatedFilterSettings);
    storeSearchFilter(updatedFilterSettings);
    setSearchFilter(updatedFilterSettings);
  };

  React.useEffect(() => {
    if (activeFilter) setSearchName(activeFilter.name);
  }, [activeFilter]);

  React.useEffect(() => {
    if (activeFilter) {
      setSearchName(activeFilter.name);
      setPrompt(activeFilter.prompt ?? '');
      // save default setting that can be reverted to...
      setOriginalFilterSettings(activeFilter.settings);
    } else {
      setSearchName('');
      setPrompt('');
    }
  }, [activeFilter, setOriginalFilterSettings]);

  React.useEffect(() => {
    if (initialState?.search === '' && searchTerms !== '') {
      const updatedInitialState = {
        ...initialState,
        search: searchTerms,
      };
      setInitialState(updatedInitialState);
      setIsFirstLoad(true);
    } else if (isDiff) {
      setIsFirstLoad(false);
    } else if (initialState?.search === '' && searchTerms === '') {
      setIsFirstLoad(true);
    }
  }, [initialState, isDiff, search, searchTerms]);

  const updateSearch = React.useCallback(
    async (name?: string, generatedSettings?: IFilterSettingsModel) => {
      // Create a new search object with updated searchTerms
      const updatedSearch = generatedSettings ?? {
        ...search,
        search: searchTerms ?? search.search,
      };
      const settings = filterFormat(updatedSearch);
      const query = genQuery(settings);

      const filter: IFilterModel = {
        ...activeFilter,
        id: activeFilter?.id ?? 0,
        name: name ?? activeFilter?.name ?? '',
        description: activeFilter?.description ?? '',
        sortOrder: activeFilter?.sortOrder ?? 0,
        isEnabled: activeFilter?.isEnabled ?? true,
        query,
        settings,
        prompt: prompt || undefined,
        reports: activeFilter?.reports ?? [],
        folders: activeFilter?.folders ?? [],
      };

      if (filter.id) {
        if (!filter.name) {
          toast.error('Please enter a name for your search.');
          return;
        }
        await updateFilter(filter)
          .then((data) => {
            toast.success(`${data.name} has successfully been updated.`);
            storeFilter(data);
            storeSearchFilter(data.settings);
            const updatedInitialState: IFilterSettingsModel = {
              ...data.settings,
              search: data.settings.search,
            };
            setInitialState(updatedInitialState);
          })
          .catch(() => {});
      }
    },
    [
      search,
      genQuery,
      activeFilter,
      prompt,
      updateFilter,
      storeFilter,
      storeSearchFilter,
      searchTerms,
    ],
  );

  const addSearch = React.useCallback(
    async (name: string, generatedSettings?: IFilterSettingsModel) => {
      const updatedSearch = generatedSettings ?? {
        ...search,
        search: searchTerms ?? search.search,
      };
      const settings = filterFormat(updatedSearch);
      const query = genQuery(settings);
      const filter: IFilterModel = {
        ...defaultFilter,
        name,
        query,
        settings,
        prompt: prompt || undefined,
      };

      if (!name) {
        toast.error('Please enter a name for your search.');
        return;
      }
      if (myFilters.some((f) => f.name === name)) {
        toast.error('A filter with this name already exists.');
        return;
      }
      await addFilter(filter)
        .then((data) => {
          toast.success(`${data.name} has successfully been created.`);
          storeFilter(data);
          storeSearchFilter(data.settings);
          setInitialState(null);
          navigate(`/search/advanced/${data.id}`);
        })
        .catch(() => {});
    },
    [
      search,
      searchTerms,
      prompt,
      genQuery,
      myFilters,
      addFilter,
      storeFilter,
      storeSearchFilter,
      navigate,
    ],
  );

  /** Use the filter the AI generated from the prompt, and run it. */
  const applyAIResult = React.useCallback(
    (result: IFilterAIGenerateResultModel, text: string, runSearch = true) => {
      // Display preferences are not filter criteria; keep the user's.
      const settings: IFilterSettingsModel = {
        ...result.settings,
        boldKeywords: search.boldKeywords,
      };
      setPrompt(text);
      setSearchTerms(settings.search ?? '');
      if (runSearch) onSearch?.(settings);
      storeSearchFilter(settings);
      setSearchFilter(settings);
    },
    [search.boldKeywords, onSearch, storeSearchFilter, setSearchFilter],
  );

  const { confirmAction, confirmation } = useFilterAIGenerationGuard(
    prompt,
    { ...search, search: searchTerms },
    async (request) => {
      const generated = await generateFilter(request);
      return {
        ...generated,
        settings: { ...generated.settings, boldKeywords: search.boldKeywords },
      };
    },
    (result, text) => applyAIResult(result, text, false),
    AppButton,
  );

  const isAdvancedQueryType =
    (search.queryType ?? AdvancedQueryOperatorOptions.simpleQueryString) ===
    AdvancedQueryOperatorOptions.queryString;

  const handleSearchTermsChanged = React.useCallback(
    (value: string) => {
      setSearchTerms(value);
      const updatedFilterSettings = {
        ...search,
        search: value,
      };
      if (isAdvancedQueryType) {
        validateQuery?.(updatedFilterSettings);
      }
    },
    [search, isAdvancedQueryType, validateQuery],
  );

  const QueryValidateContent = React.useCallback(() => {
    if (
      queryValidateResult.length > 0 &&
      (queryValidateResult[0].startsWith('ERROR:') || queryValidateResult[0].startsWith('WARNING:'))
    ) {
      return (
        <div className="query-title-container">
          <span className="query-title-label">{'Search Query Validation Failed'}</span>
          {queryValidateResult[0].startsWith('ERROR:') ? (
            <>
              <FaCircleXmark className="query-icon-red" />
              <span className="query-text-red">{'Error:'}</span>
            </>
          ) : (
            <>
              <FaExclamationCircle className="query-icon-orange" />
              <span className="query-text-orange">{'Warning:'}</span>
            </>
          )}
        </div>
      );
    }
    return (
      <div className="query-title-container">
        <span className="query-title-label">{'Search Query Validation Pass'}</span>
        <FaCircleCheck className="query-icon-green" />
      </div>
    );
  }, [queryValidateResult]);

  const ParsedQueryErrorTextArea = React.useCallback(() => {
    let warningReason = '';
    if (queryValidateResult.length > 0 && queryValidateResult[0].startsWith('WARNING:')) {
      const warning = queryValidateResult[0];
      const regexIndexString = / index: \d+/;
      const regexIndex = /\d+/;
      const indexStringMatch = warning.match(regexIndexString);
      const indexMatch = indexStringMatch ? indexStringMatch[0].match(regexIndex) : null;
      const startIndex = indexMatch ? Number(indexMatch[0]) : -1;
      if (startIndex >= 0) {
        let endIndex = searchTerms.substring(startIndex + 1).indexOf(' ');
        endIndex = startIndex + (endIndex === -1 || endIndex === 0 ? 1 : endIndex);
        const textBeforeWarning = searchTerms.substring(0, startIndex + 1);
        const textWarning = searchTerms.substring(startIndex + 1, endIndex + 1);
        const textAfterWarning = searchTerms.substring(endIndex + 1);
        return (
          <div className="text-container-validate-div">
            <span className="text-area-warning">{warningReason}&nbsp;</span>
            <span className="text-area-text">{textBeforeWarning}&nbsp;</span>
            <span className="text-area-warning text-area-underline">{textWarning}&nbsp;</span>
            <span className="text-area-text">{textAfterWarning}</span>
          </div>
        );
      }
    }
    if (queryValidateResult.length > 0 && queryValidateResult[0].startsWith('ERROR:')) {
      const regexColumn = /, column \d+/;
      const regexNumber = /\d+/;
      const errorString = queryValidateResult[0];
      const colMatch = errorString.match(regexColumn);
      const colIndex = colMatch ? colMatch[0].match(regexNumber) : null;
      const endIndex = colIndex ? Number(colIndex[0]) : 0;
      if (endIndex > 0) {
        const startIndex = searchTerms.substring(0, endIndex).lastIndexOf(' ');
        const textBeforeError = searchTerms.substring(0, startIndex + 1);
        const textError = searchTerms.substring(startIndex + 1, endIndex);
        const textAfterError = searchTerms.substring(endIndex);
        return (
          <div className="text-container-validate-div">
            <span className="text-area-text">{textBeforeError}&nbsp;</span>
            <span className="text-area-error text-area-underline">{textError}&nbsp;</span>
            <span className="text-area-text">{textAfterError}</span>
          </div>
        );
      }
    }
    return null;
  }, [queryValidateResult, searchTerms]);

  const QueryValidateSection = React.useCallback(() => {
    if (isAdvancedQueryType) {
      if (
        queryValidateResult.length > 0 &&
        (queryValidateResult[0].startsWith('ERROR:') ||
          queryValidateResult[0].startsWith('WARNING:'))
      ) {
        return (
          <ExpandableRow
            icon={<></>}
            iconTitle={<QueryValidateContent />}
            title={''}
            hasValues={false}
          >
            <Row alignItems="center" justifyContent="space-between">
              <ParsedQueryErrorTextArea />
            </Row>
          </ExpandableRow>
        );
      }
      return (
        <Row alignItems="center" justifyContent="space-between" className="query-validate-row">
          <QueryValidateContent />
        </Row>
      );
    }
    return <></>;
  }, [ParsedQueryErrorTextArea, QueryValidateContent, isAdvancedQueryType, queryValidateResult]);

  const toggleAdvancedQueryOperator = React.useCallback(() => {
    if (!search.queryType) {
      storeSearchFilter({
        ...search,
        queryType: AdvancedQueryOperatorOptions.queryString,
      });
    } else if (search.queryType === AdvancedQueryOperatorOptions.simpleQueryString) {
      storeSearchFilter({
        ...search,
        queryType: AdvancedQueryOperatorOptions.queryString,
      });
    } else {
      storeSearchFilter({
        ...search,
        queryType: AdvancedQueryOperatorOptions.simpleQueryString,
      });
    }
  }, [search, storeSearchFilter]);

  return (
    <styled.AdvancedSearch expanded={expanded}>
      {confirmation}
      <PageSection
        ignoreLastChildGap
        header={
          <Row className="top-bar">
            <div className="title">{activeFilter ? 'Modify Search' : 'Advanced Search'}</div>
            <Row className="tools">
              <div className="back-text" onClick={() => navigate('/search')}>
                Back to basic
              </div>
              {expanded ? (
                <FaDownLeftAndUpRightToCenter
                  className="minimize"
                  onClick={() => setExpanded(false)}
                />
              ) : (
                <FaUpRightAndDownLeftFromCenter
                  className="expand"
                  onClick={() => setExpanded(true)}
                />
              )}
              <IoMdRefresh
                className="reset"
                data-tooltip-id="main-tooltip"
                data-tooltip-content="Reset filters"
                onClick={() => {
                  let resetFilter = activeFilter
                    ? ({
                        ...defaultAdvancedSearch,
                        ...originalFilterSettings,
                      } as IFilterSettingsModel)
                    : {
                        ...search,
                        ...defaultAdvancedSearch,
                        search: search.search,
                      };
                  storeSearchFilter(resetFilter);
                }}
              />
            </Row>
          </Row>
        }
      >
        <Show visible={expanded}>
          <div className="main-search-body">
            {/* CURRENTLY VIEWED SEARCH NAME IF PRESENT */}
            <Show visible={!!activeFilter}>
              <div className="viewed-name">
                <FaBookmark />
                <div className="filter-name">{activeFilter?.name}</div>
                <div className="status">
                  {!isFirstLoad && !isDiff ? (
                    <>
                      <FaCheck className="status_check_mark" />
                      <span className="status_saved">Saved</span>
                    </>
                  ) : !isFirstLoad && isDiff ? (
                    <button
                      className="status_save_changes"
                      onClick={() =>
                        confirmAction('save', (settings) => updateSearch(undefined, settings))
                      }
                    >
                      Save Changes
                    </button>
                  ) : (
                    ' '
                  )}
                </div>
              </div>
            </Show>
            {/* SEARCH TERMS, OR AN AI PROMPT THAT GENERATES THE SEARCH */}
            <Col className="section search-accordion">
              <AccordionPanel
                id="search-panel"
                icon={<FaMagnifyingGlass />}
                title="Search"
                open={openPanel === 'search'}
                hasValues={!!searchTerms}
                onToggle={() => setOpenPanel(openPanel === 'search' ? undefined : 'search')}
              >
                {/* SEARCH FOR: */}
                <Row className="search-for-row">
                  <label className="search-in-label">Search for:</label>
                  <ElasticQueryHelp queryType={search.queryType} />
                  <Col className="text-area-container">
                    <Editor
                      id="searchText"
                      value={searchTerms}
                      onValueChange={handleSearchTermsChanged}
                      highlight={(code: any) => {
                        return highlight(code, languages.sql, 'sql');
                      }}
                      className="text-area-editor"
                      padding={8}
                      style={{ overflow: 'hidden', resize: 'vertical' }}
                    />
                  </Col>
                </Row>
                <Col className="section">
                  <QueryValidateSection />
                </Col>
                <Row className="search-for-row">
                  <Col className="text-area-container space-top">
                    <SearchInGroup />
                  </Col>
                </Row>
                <Col className="section">
                  <ExpandableRow icon={<FaGears />} title="Advanced Options:" hasValues={false}>
                    <Row alignItems="center" justifyContent="space-between">
                      Default operator:
                      <div className="radio-group">
                        <RadioGroup
                          name="searchOperator"
                          value={
                            SearchOperatorOptions[
                              search.defaultOperator as keyof typeof SearchOperatorOptions
                            ] ?? SearchOperatorOptions.and
                          }
                          onChange={(val) => {
                            storeSearchFilter({
                              ...search,
                              defaultOperator: val.target.value.toLocaleLowerCase() as
                                | 'and'
                                | 'or'
                                | undefined,
                            });
                          }}
                          options={[SearchOperatorOptions.and, SearchOperatorOptions.or]}
                          required={true}
                          direction={'row'}
                        />
                      </div>
                      <InfoDefaultOperator />
                    </Row>
                    <Row alignItems="center" justifyContent="space-between">
                      Advanced query operators:
                      <div>
                        <ToggleButton
                          on={<FaToggleOn />}
                          off={<FaToggleOff />}
                          onClick={() => toggleAdvancedQueryOperator()}
                          value={isAdvancedQueryType}
                          width="25px"
                          height="25px"
                          label=""
                        />
                      </div>
                      <InfoAdvancedQueryOperators />
                    </Row>
                  </ExpandableRow>
                </Col>
              </AccordionPanel>
              <AccordionPanel
                id="ai-prompt-panel"
                icon={<FaWandMagicSparkles />}
                title="AI Prompt"
                open={openPanel === 'ai'}
                hasValues={!!prompt || !!search.extendedQuery || !!search.collapseField}
                onToggle={() => setOpenPanel(openPanel === 'ai' ? undefined : 'ai')}
              >
                <Col className="ai-prompt-section">
                  <FilterAIPrompt
                    label=""
                    editor="textarea"
                    prompt={prompt}
                    settings={{ ...search, search: searchTerms }}
                    onPromptChange={setPrompt}
                    onGenerate={generateFilter}
                    onChat={chatFilter}
                    onBuildQuery={(settings) => genQuery(filterFormat(settings))}
                    onSearch={(query, searchUnpublished) =>
                      findContentWithElasticsearch(query, searchUnpublished)
                    }
                    renderManualFilter={(props) => <ManualSearchForm {...props} />}
                    queryHint="Edits here only change the stories listed; the saved search rebuilds its query from its criteria."
                    onApply={(result, text) => applyAIResult(result, text)}
                    buttonComponent={AppButton}
                  />
                  <Show visible={!!search.collapseField}>
                    <Row
                      className="ai-conditions"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span>One story per {search.collapseField}</span>
                      <button
                        type="button"
                        onClick={() => storeSearchFilter({ ...search, collapseField: undefined })}
                        title="Return every matching story instead of one per value"
                      >
                        Remove
                      </button>
                    </Row>
                  </Show>
                  <Show visible={!!search.extendedQuery}>
                    <Row
                      className="ai-conditions"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span title={JSON.stringify(search.extendedQuery, null, 2)}>
                        Includes AI-added conditions
                      </span>
                      <button
                        type="button"
                        onClick={() => storeSearchFilter({ ...search, extendedQuery: undefined })}
                        title="Remove the conditions the AI added that the options below cannot show"
                      >
                        Remove
                      </button>
                    </Row>
                  </Show>
                </Col>
              </AccordionPanel>
            </Col>
            <div className="search-in-group space-top"></div>
            <Col className="section top-spacer">
              <div className="narrow-filter-header">
                <label className="search-in-label">Narrow your results by: </label>
                <div className="toggle-group-container">
                  <ToggleGroup
                    defaultSelected={displayFiltersAsDropdown ? 'sel' : 'chk'}
                    options={[
                      {
                        id: 'chk',
                        label: '',
                        icon: <FaCheckSquare />,
                        onClick: () => handleChangeDisplayFiltersAsDropdown(false),
                      },
                      {
                        id: 'sel',
                        label: '',
                        icon: <FaCaretSquareDown />,
                        onClick: () => handleChangeDisplayFiltersAsDropdown(true),
                      },
                    ]}
                  />
                  <ToggleFilterStyleInfo />
                </div>
              </div>
              <AdvancedSearchSections
                displayFiltersAsDropdown={displayFiltersAsDropdown}
                isAdmin={!!isAdmin}
              />
            </Col>

            <Row>
              <Col className="section top-spacer">
                <label className="search-in-label">Display options:</label>
                {/* SEARCH RESULT SETTINGS SECTION */}
                <ExpandableRow
                  icon={<IoIosCog />}
                  title="Search result options"
                  hasValues={checkDisplayOptions()}
                >
                  <MoreOptions />
                </ExpandableRow>
              </Col>
            </Row>
          </div>
          {/* FOOTER */}
          <Row className="adv-toolbar">
            <div className="label">SAVE:</div>
            <Text
              onChange={(e) => {
                setSearchName(e.target.value);
              }}
              name="searchName"
              value={searchName}
            />
            <button
              className="save-cloud"
              onClick={() =>
                confirmAction('save', (settings) =>
                  activeFilter?.id
                    ? updateSearch(searchName, settings)
                    : addSearch(searchName, settings),
                )
              }
              title="Save"
            >
              <FaSave />
            </button>
            {activeFilter?.id && (
              <button
                className="save-cloud"
                onClick={() => confirmAction('save', (settings) => addSearch(searchName, settings))}
                title="Add new"
              >
                <FaSquarePlus />
              </button>
            )}
            <Button onClick={() => confirmAction('search', handleSearch)} className="search-button">
              Search
              <FaPlay />
            </Button>
          </Row>
        </Show>
      </PageSection>
    </styled.AdvancedSearch>
  );
};
